import React, { useState, useEffect } from "react";
import { localPhone } from "@/lib/contacts";
import { motion } from "framer-motion";
import {
  Check,
  Diamond,
  Crown,
  Medal,
  ArrowRight,
  Phone,
  Mail,
  CreditCard,
  Building2,
  Loader2,
  Copy,
  Plane,
  Star,
  Shield,
  Briefcase,
  GraduationCap,
  Heart,
  Globe2,
  BookUser,
  Home,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { createCheckout, createBankOrder, getPackages, getCurrency } from "@/lib/paymentsApi";

const HERO_IMG =
  "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920&ixlib=rb-4.1.0";

// Packages come from the ERP (Settings > Website packages); icons by key.
const ICONS = { diamond: Diamond, crown: Crown, medal: Medal, plane: Plane, star: Star, shield: Shield, briefcase: Briefcase,
  graduation: GraduationCap, heart: Heart, globe: Globe2, passport: BookUser, home: Home };

const CURRENCIES = [
  { code: "eur", label: "EUR €" },
  { code: "usd", label: "USD $" },
  { code: "aoa", label: "AOA Kz" },
];

const fmt = (cur, amt) => {
  if (amt == null) return "";
  const n = Number(amt);
  if (cur === "aoa") return `Kz ${n.toLocaleString("pt-PT", { maximumFractionDigits: 0 })}`;
  const s = n.toLocaleString("en-US", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
  return `${cur === "usd" ? "$" : "€"}${s}`;
};

const fade = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export const PackagesPage = () => {
  const { t, i18n } = useTranslation();
  const [currency, setCurrency] = useState("eur");
  const [prices, setPrices] = useState({}); // { gold: {eur,usd,aoa}, silver: {...} }
  const [tiers, setTiers] = useState(null);
  const lang = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  const tr = (v) => (v && (v[lang] || v.en || v.pt)) || "";
  const [openTier, setOpenTier] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [method, setMethod] = useState("card");
  const [submitting, setSubmitting] = useState(false);
  const [bankResult, setBankResult] = useState(null);

  useEffect(() => {
    getPackages()
      .then((d) => {
        const map = {};
        (d.packages || []).forEach((p) => { map[p.id] = p.prices; });
        setPrices(map);
        setTiers((d.packages || []).map((p) => ({ ...p, name: tr(p.name) || p.tier, contact: p.cta === "contact" })));
      })
      .catch(() => setTiers([]));
    getCurrency()
      .then((d) => { if (d?.currency) setCurrency(d.currency); })
      .catch(() => {});
  }, []);

  const priceOf = (tierId, cur = currency) => prices[tierId]?.[cur];

  const openBuy = (tier) => {
    setForm({ name: "", email: "", phone: "" });
    setMethod(currency === "aoa" ? "bank" : "card");
    setBankResult(null);
    setOpenTier(tier);
  };

  const closeBuy = () => {
    setOpenTier(null);
    setBankResult(null);
  };

  const canSubmit = form.name.trim() && /\S+@\S+\.\S+/.test(form.email);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error(t("packagesPage.dialog.validationError"));
      return;
    }
    setSubmitting(true);
    try {
      if (method === "card") {
        const res = await createCheckout({
          package_id: openTier.id,
          origin_url: window.location.origin,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          currency,
          language: i18n.resolvedLanguage || i18n.language || "en",
        });
        window.location.href = res.checkout_url;
      } else {
        const res = await createBankOrder({
          package_id: openTier.id,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          language: i18n.resolvedLanguage || i18n.language || "en",
        });
        setBankResult(res);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("packagesPage.dialog.genericError"));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success(t("packagesPage.dialog.copied"));
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden" data-testid="packages-hero">
          <div className="absolute inset-0">
            <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-venka-ink via-venka-ink/85 to-venka-ink/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          </div>
          <div className="relative mx-auto max-w-7xl px-6 py-24 lg:py-28">
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-venka-green-light mb-3">{t("packagesPage.eyebrow")}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-white max-w-3xl" data-testid="packages-title">
              {t("packagesPage.titleA")} <span className="text-brand-gradient">{t("packagesPage.titleHighlight")}</span>{t("packagesPage.titleB")}
            </h1>
            <p className="mt-5 text-base text-slate-300 max-w-xl leading-relaxed">
              {t("packagesPage.subtitle")}
            </p>
          </div>
        </section>

        {/* Tiers */}
        <section className="mx-auto max-w-7xl px-6 py-20" data-testid="packages-grid">
          {/* Currency switcher */}
          <div className="flex items-center justify-center gap-2 mb-10" data-testid="currency-switcher">
            <span className="text-xs text-slate-400 mr-1">{t("packagesPage.showPricesIn")}</span>
            {CURRENCIES.map((c) => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
                  currency === c.code
                    ? "brand-gradient text-white border-transparent"
                    : "border-white/15 text-slate-300 hover:bg-white/5"
                }`}
                data-testid={`currency-switch-${c.code}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {!tiers && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-venka-green-light" /></div>}
          <div className={`grid gap-6 items-stretch ${tiers && tiers.length === 2 ? "lg:grid-cols-2 max-w-4xl mx-auto" : tiers && tiers.length === 1 ? "max-w-md mx-auto" : "md:grid-cols-2 lg:grid-cols-3"}`}>
            {(tiers || []).map((tier, i) => {
              const Icon = ICONS[tier.icon] || Star;
              const price = priceOf(tier.id);
              const priceLabel = tier.cta === "buy" ? fmt(currency, price) : price != null ? `${t("packagesPage.from", "From")} ${fmt(currency, price)}` : t("packagesPage.contactSales");
              const badge = tr(tier.badge) || (tier.highlight ? t("packagesPage.mostComplete") : "");
              return (
                <motion.div
                  key={tier.id}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  variants={fade}
                  custom={i}
                  className={`relative rounded-3xl border p-8 flex flex-col ${
                    tier.highlight
                      ? "border-venka-green/40 bg-card shadow-2xl shadow-venka-blue/10"
                      : "border-white/10 bg-card"
                  }`}
                  data-testid={`package-card-${tier.id}`}
                >
                  {badge && (
                    <span className="absolute -top-3 left-8 rounded-full brand-gradient px-3 py-1 text-[11px] font-semibold text-white uppercase tracking-wide">
                      {badge}
                    </span>
                  )}
                  <div className="flex items-center gap-3">
                    <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${tier.highlight ? "brand-gradient text-white" : "bg-venka-blue/15 text-venka-green-light"}`}>
                      <Icon className="h-6 w-6" strokeWidth={1.8} />
                    </span>
                    <h2 className="font-serif text-2xl text-white">{tier.name}</h2>
                  </div>
                  {tr(tier.tagline) && <p className="mt-4 text-sm text-slate-400 leading-relaxed">{tr(tier.tagline)}</p>}
                  <div className="mt-6">
                    <p className="font-serif text-4xl text-white" data-testid={`package-price-${tier.id}`}>{priceLabel}</p>
                    {tier.cta === "buy" && <p className="text-xs text-slate-400 mt-1">{t("packagesPage.oneTime")}</p>}
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {(tier.features || []).map((f, k) => (
                      <li key={k} className="flex items-start gap-2.5 text-sm text-slate-200">
                        <Check className="h-4 w-4 mt-0.5 text-venka-green-light shrink-0" /> {tr(f)}
                      </li>
                    ))}
                  </ul>
                  {tier.cta === "contact" ? (
                    <div className="mt-8 grid grid-cols-2 gap-2">
                      <Button asChild className="rounded-full brand-gradient text-white hover:opacity-90" data-testid={`package-${tier.id}-call`}>
                        <a href={`tel:${localPhone().tel}`}><Phone className="mr-1.5 h-4 w-4" /> {t("packagesPage.call")}</a>
                      </Button>
                      <Button asChild variant="outline" className="rounded-full border-white/20 text-slate-100 hover:bg-white/5" data-testid={`package-${tier.id}-email`}>
                        <a href={`mailto:sales@venkawe.com?subject=${encodeURIComponent(`${tier.name} package`)}`}><Mail className="mr-1.5 h-4 w-4" /> {t("packagesPage.email")}</a>
                      </Button>
                    </div>
                  ) : tier.cta === "apply" ? (
                    <Button asChild className="mt-8 rounded-full brand-gradient text-white hover:opacity-90" data-testid={`package-apply-${tier.id}`}>
                      <Link to={tier.link || "/apply"}>{t("packagesPage.applyNow", "Start now")} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                    </Button>
                  ) : (
                    <Button
                      onClick={() => openBuy(tier)}
                      className="mt-8 rounded-full brand-gradient text-white hover:opacity-90"
                      data-testid={`package-buy-${tier.id}`}
                    >
                      {t("packagesPage.buy")} {tier.name} — {priceLabel} <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  )}
                </motion.div>
              );
            })}
          </div>
          <p className="mt-8 text-center text-xs text-slate-500">
            {t("packagesPage.footerNote")}
          </p>
        </section>
      </main>
      <Footer />

      {/* Buy dialog */}
      <Dialog open={!!openTier} onOpenChange={(o) => !o && closeBuy()}>
        <DialogContent className="bg-card border-white/10 text-slate-100 max-w-md" data-testid="buy-dialog">
          {!bankResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">
                  {openTier?.name} {t("packagesPage.dialog.packageWord")} — {openTier && fmt(currency, priceOf(openTier.id))}
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  {t("packagesPage.dialog.desc")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Input
                  placeholder={t("packagesPage.dialog.fullName")}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-950/60 border-white/15"
                  data-testid="buy-name-input"
                />
                <Input
                  type="email"
                  placeholder={t("packagesPage.dialog.emailAddr")}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-slate-950/60 border-white/15"
                  data-testid="buy-email-input"
                />
                <Input
                  placeholder={t("packagesPage.dialog.phoneOpt")}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-slate-950/60 border-white/15"
                  data-testid="buy-phone-input"
                />
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setMethod("card")}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-colors ${method === "card" ? "border-venka-green/60 bg-venka-blue/15 text-white" : "border-white/15 text-slate-300"}`}
                    data-testid="buy-method-card"
                  >
                    <CreditCard className="h-4 w-4" /> {t("packagesPage.dialog.card")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod("bank")}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-colors ${method === "bank" ? "border-venka-green/60 bg-venka-blue/15 text-white" : "border-white/15 text-slate-300"}`}
                    data-testid="buy-method-bank"
                  >
                    <Building2 className="h-4 w-4" /> {t("packagesPage.dialog.bankMethod")}
                  </button>
                </div>
                {method === "card" && currency === "aoa" && (
                  <p className="text-xs text-amber-300/90" data-testid="aoa-card-note">
                    {t("packagesPage.dialog.aoaNote", { price: openTier ? fmt("usd", priceOf(openTier.id, "usd")) : "" })}
                  </p>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !canSubmit}
                  className="w-full rounded-full brand-gradient text-white hover:opacity-90 mt-1"
                  data-testid="buy-submit-button"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : method === "card" ? t("packagesPage.dialog.payCard") : t("packagesPage.dialog.getBank")}
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{t("packagesPage.dialog.bankTitle")}</DialogTitle>
                <DialogDescription className="text-slate-400">
                  {t("packagesPage.dialog.bankRef")} <span className="text-venka-green-light font-semibold" data-testid="bank-reference">{bankResult.reference}</span> — {openTier?.name} · {fmt("aoa", bankResult.order?.amount)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm" data-testid="bank-details">
                {[
                  ["bankName", bankResult.bank_details.bank_name],
                  ["holder", bankResult.bank_details.account_holder],
                  ["accountNumber", bankResult.bank_details.account_number],
                  ["iban", bankResult.bank_details.iban],
                  ["currency", bankResult.bank_details.currency],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-2 rounded-lg bg-slate-950/50 border border-white/10 px-3 py-2">
                    <span className="text-slate-400">{t(`packagesPage.dialog.${k}`)}</span>
                    <span className="flex items-center gap-2 text-slate-100 text-right">
                      {v}
                      <button onClick={() => copy(String(v))} className="text-slate-400 hover:text-white"><Copy className="h-3.5 w-3.5" /></button>
                    </span>
                  </div>
                ))}
                <p className="text-xs text-slate-400 pt-2">{bankResult.bank_details.instructions}</p>
                <Button onClick={closeBuy} className="w-full rounded-full brand-gradient text-white mt-2" data-testid="bank-done-button">{t("packagesPage.dialog.done")}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesPage;
